import React from 'react';
import { Redirect, Link } from 'react-router-dom';
import { Query } from 'react-apollo';
import { currySC, cns } from '@food/css-manager';
import { IPageData } from '../DefaultPageData';
import { canView } from '../../utils/canView';
import { isLogged } from '@food/auth/build/dist/logic/auth';
import { generateMagazineReadUrl, generateMagazineDetailUrl } from '../../utils/urls';
import { Spinner } from '../Spinner';
import { EditorialSection, Magazine } from '../../server-types';
import { MagazineCollectionHistory } from '../MagazineCollectionHistory';
import { useTranslation } from 'react-i18next';
import { magazineReadQuery, magazineStyleClass } from './MagazineRead';
import { MagazineSlideshow } from '../MagazineSlideshow';
import { magazineLastIssuesSliderQuery } from '../../utils/fragments';
// import { SubscriptionBannerStatusAccess } from '../SubscriptionBannerStatusAccess';
// import { SubscriptionBannerStatusActive } from '../SubscriptionBannerStatusActive';
// import { hasSubscription } from '../../utils/hasSubscription';
import { Icon } from '@food/ui';
import * as Sentry from '@sentry/browser';
import { useUserMagazineData } from '../../utils/hooks';
import { config } from '../../utils/config';
import { EditorialSectionOrCollectionHeader } from '../EditorialSectionOrCollectionHeader';
import { DefaultLayout } from '../../utils/layout';
import gql from 'graphql-tag';

const sc = currySC(magazineStyleClass);

const editorialSectionQuery = gql`
    query EditorialSectionQuery($editorialSectionId: ID!) {
        editorialSection: node(id: $editorialSectionId) {
            ... on EditorialSection {
                id
                name,
				color,
                iconMedia {
                    id,
                    vecThumbUrl,
                    mediumThumbUrl,
					origUrl
                }
            }
        }
    }
`;

export const PreviewRead: React.FC<IPageData & {
	magazineId: string;
}> = ({ magazineId, layout }) => {
	const { t } = useTranslation();
	const { loading: loading1, userMagazineData } = useUserMagazineData();
	return (
		<Query<{ magazine: Magazine }, { magazineId: string }>
			query={magazineReadQuery}
			variables={{ magazineId }}
		>
			{({ error, loading, data }) => {
				if (error) {
					// TODO
					console.error(error);
					return <span>{error}</span>;
				}

				if (loading || loading1 || !data) {
					return <Spinner />;
				}

				const { magazine } = data;

				const { MagazineCollection: collection } = magazine;
				let { previewUrl, issueNumber, name } = magazine;

				const hasVisibility = canView({
					visibility: magazine.customVisibilityType || collection.visibilityType,
					args: magazine.customVisibilityType || collection.visibilityArgs,
					user: userMagazineData,
					isLogged: isLogged(),
				});
				// const isSubscribed = hasSubscription({ user: user });

				const issue = (issueNumber ? `N.${issueNumber} ` : '') + name;
				document.title = "Preview: " + issue + " - " + t(collection.BrandSf) + " - " + collection.name + " - Knowledge Center";

				if (hasVisibility) {
					return <Redirect to={generateMagazineReadUrl(magazine.id)} />;
				}

				if (!previewUrl) {
					// in questo caso segnalo il problema a sentry!
					Sentry.captureException(new Error('Magazine senza url anteprima calameo'));
				}

				let pre = '?';
				if (previewUrl.indexOf('?') !== -1) {
					pre = '&';
				}
				previewUrl += `${pre}utm_source=kc`;

				// cerco editorial section da config (by name)
				let editorialSectionId: any = null;

				if (magazine.category && (config.KNOWLEDGECENTER_SECTIONS || config.KNOWLEDGECENTER_MAGAZINECOLLECTIONS || config.KNOWLEDGECENTER_VERTICALS)) {
					const filteredCategoryFromConfig = [...config.KNOWLEDGECENTER_SECTIONS, ...config.KNOWLEDGECENTER_MAGAZINECOLLECTIONS, ...config.KNOWLEDGECENTER_VERTICALS].filter((v) => {
						return (typeof (v) !== 'object') ?
							false :
							(!v.name) ?
								false :
								(magazine.category && v.name.toLowerCase() === magazine.category.toLowerCase()) ?
									true :
									false;
					});
					editorialSectionId = (filteredCategoryFromConfig.length) ? filteredCategoryFromConfig[0].id : null;
				}
				// cerco collection da config (by id)
				let collectionLabel: any = null;
				if (magazine.MagazineCollection && config.KNOWLEDGECENTER_MAGAZINECOLLECTIONS) {
					const filteredCollectionFromConfig = [...config.KNOWLEDGECENTER_MAGAZINECOLLECTIONS].filter((v) => {
						return (typeof (v) !== 'object') ?
							false :
							(!v.name) ?
								false :
								(magazine.MagazineCollection.id && v.id === magazine.MagazineCollection.id) ?
									true :
									false;
					});
					const collectionFromConfig = (filteredCollectionFromConfig.length) ? filteredCollectionFromConfig[0] : null;
					collectionLabel = (collectionFromConfig && collectionFromConfig.label) ? collectionFromConfig.label : magazine.MagazineCollection.name;
				}

				return (
					<div className={cns(magazineStyleClass, sc('layout', layout))}>
						{/*{hasVisibility && !isSubscribed && (
										<SubscriptionBannerStatusAccess />
									)}
									<SubscriptionBannerStatusActive user={user} />*/}

						{editorialSectionId ? <Query<{ editorialSection: EditorialSection }, { editorialSectionId: string }>
							query={editorialSectionQuery}
							variables={{ editorialSectionId }}
						>
							{({ error, loading, data }) => {
								if (error) {
									console.error(error);
									return <span>error</span>;
								}

								if (loading || !data) {
									return <Spinner />;
								}

								const { editorialSection } = data;

								// recupero le informazioni dal config
								const configEditorialSection = config.KNOWLEDGECENTER_VERTICALS.concat(config.KNOWLEDGECENTER_SECTIONS).filter((v: any) => typeof (v) === 'object' ? (v.id === editorialSection.id) : v === editorialSection.id)[0];

								let aLinks = [] as { url: string; name: string, iframe?: boolean }[];
								if (configEditorialSection.news_link) {
									aLinks.push({
										url: '' + configEditorialSection.news_link,
										name: 'Guarda tutte le news'
									});
								}
								if (configEditorialSection.newsletter_link) {
									aLinks.push({
										url: '' + configEditorialSection.newsletter_link,
										name: 'Iscriviti alla newsletter',
										iframe: true
									});
								}

								return (
									<EditorialSectionOrCollectionHeader
										layout={layout ? layout : DefaultLayout.DESKTOP}
										media={editorialSection.iconMedia && (editorialSection.iconMedia.vecThumbUrl || editorialSection.iconMedia.origUrl)}
										color={editorialSection.color ? editorialSection.color : null}
										title={editorialSection.name}
										links={aLinks}
									/>
								);
							}}
						</Query> :
							(collectionLabel && <EditorialSectionOrCollectionHeader
								layout={layout ? layout : DefaultLayout.DESKTOP}
								title={collectionLabel}
							/>)}
						<br /><br />
						<Link
							className={sc(`back`)}
							to={generateMagazineDetailUrl(magazine.id)}
						>
							<Icon name={'chevron_left'} className={sc('icon')} />
							{t(`magazine_back_to_summary`)}
						</Link>
						<div className={sc('iframeContainer')}>
							<iframe
								src={previewUrl}
								className={sc('iframe')}
								title={'read-iframe'}
								allowFullScreen={true}
							/>
						</div>

						<MagazineSlideshow
							layout={layout}
							query={magazineLastIssuesSliderQuery}
							variables={{}}
							excluded={[magazine.id]}
							sectionTitle={t`last_issues`}
							className={sc('section')}
						/>
						<MagazineCollectionHistory
							collectionId={collection.id}
							layout={layout}
							className={sc('section')}
						/>
					</div>
				);
			}}
		</Query>
	);
};
